"use client"

import * as React from "react"
import { toast } from "sonner"

import { Button, cn } from "@ibada/ui"
import { isZeroMoney } from "@ibada/shared-types/finance"

import { useTranslations } from "@/i18n"
import { formatMoney } from "@/features/finance/lib/finance-utils"
import { useBranchCurrency } from "@/features/finance/hooks/use-branch-currency"
import {
  ReconciliationViewerAction,
  type ReconciliationSessionDetail,
} from "@/features/finance/model/finance-close-trust-types"
import { useReconciliationTransitionMutation } from "@/features/finance/services/finance-close-trust-query"
import { useApiErrorToast } from "@/shared/hooks/use-api-error"
import { useIsMobile } from "@/shared/hooks/use-mobile"

import { ReconciliationTransitionDialog } from "./reconciliation-transition-dialog"

/** Order the actions are offered in, regardless of the order the server lists them. */
const ACTION_ORDER: ReconciliationViewerAction[] = [
  ReconciliationViewerAction.Approve,
  ReconciliationViewerAction.Reject,
  ReconciliationViewerAction.Submit,
  ReconciliationViewerAction.WithdrawToDraft,
]

const DESTRUCTIVE: ReconciliationViewerAction[] = [ReconciliationViewerAction.Reject]

/** Phone pills: green for the decision, red for a rejection, grey for stepping back. */
function mobilePillClass(action: ReconciliationViewerAction): string {
  if (action === ReconciliationViewerAction.Reject) return "bg-destructive/10 text-destructive"
  if (action === ReconciliationViewerAction.WithdrawToDraft) return "bg-card text-foreground ring-1 ring-border/70"
  return "bg-primary text-primary-foreground"
}

function buttonVariant(action: ReconciliationViewerAction): "default" | "outline" | "destructive" {
  if (action === ReconciliationViewerAction.Reject) return "destructive"
  if (action === ReconciliationViewerAction.WithdrawToDraft) return "outline"
  return "default"
}

/**
 * The lifecycle actions for a session, rendered from the server's `viewerActions`.
 *
 * Nothing here decides what the viewer may do — permission, legality and separation of duties are all
 * evaluated server-side and arrive as a list. That is deliberate: a client that derives its own actions
 * eventually shows an Approve button to the person who submitted, and being refused after clicking reads as
 * the system having lost the submission rather than as the rule it actually is.
 */
export function ReconciliationActionStrip({
  session,
  canEdit,
}: {
  session: ReconciliationSessionDetail
  /** Resolved by the finance page through the central tenant authorization registry. */
  canEdit: boolean
}) {
  const t = useTranslations("finance.reconciliation")
  const toastError = useApiErrorToast("finance")
  const transition = useReconciliationTransitionMutation()
  const branchCurrency = useBranchCurrency()
  const [pendingAction, setPendingAction] = React.useState<ReconciliationViewerAction | null>(null)
  const isMobile = useIsMobile()

  // Never "": `formatMoney` -> Intl.NumberFormat({ currency: "" }) throws, and `currency` is null for any
  // code outside the Currency enum. Same fallback the session page and the list use.
  const currency = session.currency ?? branchCurrency
  // A submission whose books do not match the statement must carry an explanation, so the dialog asks for one
  // and blocks until it is written — the API enforces the same rule, this just makes it visible first.
  const hasDifference = !isZeroMoney(session.difference, currency)

  const actions = canEdit ? ACTION_ORDER.filter((action) => session.viewerActions.includes(action)) : []
  if (actions.length === 0) return null

  const run = async (action: ReconciliationViewerAction, note: string | undefined) => {
    try {
      await transition.mutateAsync({ action, workspaceId: session.workspaceId, sessionId: session.id, note })
      // Past tense: the button label is an instruction, a toast is a result.
      toast.success(t(`toast.${action}` as never))
      setPendingAction(null)
    } catch (error) {
      toastError(error)
      setPendingAction(null)
    }
  }

  const dialogFor = (action: ReconciliationViewerAction) => {
    const isSubmit = action === ReconciliationViewerAction.Submit
    const isReject = action === ReconciliationViewerAction.Reject
    const isWithdraw = action === ReconciliationViewerAction.WithdrawToDraft
    const wantsNote = !isWithdraw

    return {
      title: t(`dialogs.${action}.title`),
      description:
        isSubmit && hasDifference
          ? t("dialogs.submit.bodyWithDifference", { difference: formatMoney(session.difference, currency) })
          : t(`dialogs.${action}.body`),
      confirmLabel: t(`dialogs.${action}.confirm`),
      noteLabel: wantsNote ? t(`dialogs.${action}.noteLabel`) : undefined,
      notePlaceholder: isSubmit || isReject ? t(`dialogs.${action}.notePlaceholder`) : undefined,
      // Rejections always need a reason; a submission needs one only when the figures disagree.
      noteRequired: isReject || (isSubmit && hasDifference),
      destructive: DESTRUCTIVE.includes(action),
    }
  }

  return (
    <div className={isMobile ? "grid auto-cols-fr grid-flow-col gap-2.5" : "flex flex-wrap items-center gap-2"}>
      {actions.map((action) =>
        isMobile ? (
          <button
            key={action}
            type='button'
            disabled={transition.isPending}
            onClick={() => setPendingAction(action)}
            className={cn(
              "flex h-[3.25rem] min-w-0 items-center justify-center truncate rounded-full px-4 text-base font-extrabold active:brightness-95 disabled:opacity-45",
              mobilePillClass(action),
            )}
          >
            {t(`actions.${action}`)}
          </button>
        ) : (
        <Button
          key={action}
          size='sm'
          variant={buttonVariant(action)}
          disabled={transition.isPending}
          onClick={() => setPendingAction(action)}
        >
          {t(`actions.${action}`)}
        </Button>
        ),
      )}

      {pendingAction ? (
        <ReconciliationTransitionDialog
          open
          onOpenChange={(open) => {
            if (!open) setPendingAction(null)
          }}
          pending={transition.isPending}
          onConfirm={(note) => void run(pendingAction, note)}
          {...dialogFor(pendingAction)}
        />
      ) : null}
    </div>
  )
}
